import { Flex, GridItem, Stack, Text } from '@chakra-ui/react'
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  faq,
  getAboutUrl,
  getBecomeATutorUrl,
  getBlogUrl,
  getBrandResourcesUrl,
  getCertificationUrl,
  getDonateUrl,
  getFingerprintUrl,
  getPartnershipsUrl,
  getPublicUrl,
} from '@/client/services/url'

import BrandMark from '@/components/Icons/BrandMark'
import OptimizedImage from '@/components/PublicImage/OptimizedImage'
import ShwLink from '@/components/ShwLink'

import ShwGrid from './ShwGrid'

const socialMediaLinks = [
  { src: 'https://www.instagram.com/schoolhouse.world/', icon: faInstagram },
  { src: 'https://twitter.com/schoolhouse_edu', icon: faTwitter },
  { src: 'https://www.facebook.com/schoolhouse.world/', icon: faFacebook },
  { src: 'https://www.youtube.com/channel/UCLu6CmthnLrjPywMYunkwJQ', icon: faYoutube },
  { src: 'https://www.linkedin.com/company/schoolhouse-world', icon: faLinkedin },
]

const footerSections = [
  {
    title: 'About',
    links: [
      { text: 'About Us', src: '/about' },
      { text: 'Partnerships', src: getPartnershipsUrl() },
      { text: 'Roadmap', src: '/roadmap' },
      { text: 'Careers', src: '/careers' },
      { text: 'Donate', src: getDonateUrl() },
    ],
  },
  {
    title: 'Tutoring',
    links: [
      { text: 'Peer Tutoring Benefits', src: getAboutUrl('peer-tutoring') },
      { text: 'Get Certified', src: getCertificationUrl('get-certified') },
      { text: 'Become a Tutor', src: getBecomeATutorUrl() },
      { text: 'Session Safety Measures', src: getAboutUrl('safety') },
    ],
  },
  {
    title: 'Community',
    links: [
      { text: 'Blog', src: getBlogUrl() },
      { text: 'Schoolhouse Stories', src: '/stories' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { text: 'Brand Resources', src: getBrandResourcesUrl() },
      { text: 'Help Desk', src: faq.getUrl() },
    ],
  },
]

export default function Footer(): JSX.Element {
  return (
    <Flex bgColor="primary.solid">
      <Flex
        width="100%"
        px={[4, 4, 6, 8]}
        pt={16}
        pb={20}
        flexDirection="column"
        maxW="1240px"
        margin="0 auto"
      >
        <Text color="primary.contrast" fontWeight="bold" ml={-1} mb={12}>
          <BrandMark textColor="primary.contrast" logoColor="primary.contrast" height="32px" />
        </Text>
        <ShwGrid>
          {footerSections.map(({ title, links }) => (
            <GridItem colSpan={[12, 12, 6, 3]} key={title}>
              <Stack direction="column" spacing="32px" mb="48px">
                <Text variant="eyebrow" fontSize="xs" fontWeight="bold" color="primary.contrast">
                  {title}
                </Text>
                <Stack direction="column" spacing="16px">
                  {links.map(({ text, src }) => (
                    <ShwLink
                      variant="unstyled"
                      color="primary.contrast"
                      _hover={{ textDecoration: 'underline' }}
                      fontSize="16px"
                      fontWeight={500}
                      href={src}
                      key={src}
                    >
                      {text}
                    </ShwLink>
                  ))}
                </Stack>
              </Stack>
            </GridItem>
          ))}
        </ShwGrid>
        <Stack direction="row" justifyContent={['flex-start', 'flex-start', 'flex-end']}>
          <ShwLink href={getFingerprintUrl()}>
            <OptimizedImage
              src={getPublicUrl('/FingerprintBadge.svg')}
              alt="Fingerprint Badge"
              originalWidth={132}
              originalHeight={50}
              displayWidth="100px"
            />
          </ShwLink>
        </Stack>
        <Stack
          direction={['column', 'column', 'row']}
          justify="space-between"
          align={['flex-start', 'flex-start', 'center']}
          spacing={[4, 4, 0]}
          mt="16px"
        >
          <Stack direction={['column', 'column', 'row']} spacing={[4, 4, 10]}>
            <Text color="primary.contrast" fontWeight={600}>
              © Schoolhouse.world
            </Text>
            <ShwLink
              variant="unstyled"
              color="primary.contrast"
              _hover={{ textDecoration: 'underline' }}
              fontSize="16px"
              fontWeight={600}
              href={getAboutUrl('terms-of-service')}
            >
              Terms of Service
            </ShwLink>
            <ShwLink
              variant="unstyled"
              color="primary.contrast"
              _hover={{ textDecoration: 'underline' }}
              fontSize="16px"
              fontWeight={600}
              href={getAboutUrl('terms')}
            >
              Terms &amp; Conditions
            </ShwLink>
            <ShwLink
              variant="unstyled"
              color="primary.contrast"
              _hover={{ textDecoration: 'underline' }}
              fontSize="16px"
              fontWeight={600}
              href={getAboutUrl('privacy')}
            >
              Privacy Policy
            </ShwLink>
            <ShwLink
              variant="unstyled"
              color="primary.contrast"
              _hover={{ textDecoration: 'underline' }}
              fontSize="16px"
              fontWeight={600}
              href={getAboutUrl('trust-safety')}
            >
              Trust &amp; Safety
            </ShwLink>
            <ShwLink
              variant="unstyled"
              color="primary.contrast"
              _hover={{ textDecoration: 'underline' }}
              fontSize="16px"
              fontWeight={600}
              href={`mailto:press@schoolhouse.world?subject=${encodeURIComponent('Press Request')}`}
            >
              Press
            </ShwLink>
          </Stack>
          <Stack direction="row" spacing={4}>
            {socialMediaLinks.map(({ icon, src }) => (
              <ShwLink
                color="primary.contrast"
                _hover={{ color: 'gray.200' }}
                fontSize="24px"
                variant="unstyled"
                href={src}
                isExternal
                key={src}
              >
                <FontAwesomeIcon icon={icon} />
              </ShwLink>
            ))}
          </Stack>
        </Stack>
      </Flex>
    </Flex>
  )
}
